import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import Button from './Button';
import LanguageSelector from './LanguageSelector';
import { LogOut as LogOutIcon, X as CloseIcon } from 'react-feather';

interface NavItem {
  path: string;
  label: string;
  icon: React.FC<{ className?: string }>;
}

interface MobileNavProps {
  navItems: NavItem[];
  onClose: () => void;
  activePath: string;
}

const MobileNav: React.FC<MobileNavProps> = ({ navItems, onClose, activePath }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      onClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-80 max-w-sm bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600">
            <h2 className="text-xl font-bold text-white">{t('common.menu')}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors duration-200"
              aria-label={t('common.close')}
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Language Selector */}
          <div className="p-6 border-b border-gray-100">
            <LanguageSelector />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <div className="space-y-2">
              {navItems?.map((item) => {
                const Icon = item.icon;
                const isActive = activePath === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={`flex items-center px-4 py-4 text-base font-medium rounded-lg transition-all duration-200 ${
                      isActive 
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border-l-4 border-green-500' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`mr-4 h-6 w-6 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Logout Button */}
          <div className="border-t border-gray-200 p-6">
            <Button 
              onClick={handleLogout} 
              variant="secondary" 
              className="w-full flex items-center justify-center py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            >
              <LogOutIcon className="mr-3 h-5 w-5" />
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileNav;